import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import { CustomDatePicker } from '../commons/datepicker';
import '../../styles/DogSurvey.css';
import { IoChevronDownOutline, IoChevronUpOutline } from "react-icons/io5";
import { FiPlus } from 'react-icons/fi';
import AssignSurveyModal from './AssignSurveyModal';
import { useToast } from '../commons/toast/ToastContext';

const DogSurveysList = ({ surveys, dogId, onSurveyAssigned, canFill = false }) => {
    const { t, i18n } = useTranslation(['dogs', 'common']);
    const toast = useToast();
    const [expandedSurvey, setExpandedSurvey] = useState(null);
    const [surveyResponses, setSurveyResponses] = useState({});
    const [loadingResponses, setLoadingResponses] = useState(false);
    const [error, setError] = useState('');
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [localSurveys, setLocalSurveys] = useState(surveys || []);
    const [responses, setResponses] = useState({});
    const [submitting, setSubmitting] = useState({});

    // Update local surveys when prop changes
    useEffect(() => {
        setLocalSurveys(surveys || []);
    }, [surveys]);

    // Fetch assigned surveys for this dog
    const fetchAssignedSurveys = async () => {
        try {
            const token = localStorage.getItem('jwt');
            const response = await axios.get(`/api/v1/survey/dog/${dogId}/assigned`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLocalSurveys(response.data || []);
        } catch (err) {
            console.error('Error fetching assigned surveys:', err);
        }
    };

    // Handle survey assignment
    const handleSurveyAssigned = async () => {
        await fetchAssignedSurveys();
        if (onSurveyAssigned) {
            onSurveyAssigned();
        }
    };

    useEffect(() => {
        if (expandedSurvey) {
            fetchSurveyResponses(expandedSurvey, dogId);
        }
    }, [expandedSurvey, dogId]);

    const fetchSurveyResponses = async (surveyId, dogId) => {
        if (surveyResponses[surveyId]) {
            // Already fetched
            return;
        }

        setLoadingResponses(true);
        setError('');

        try {
            const response = await axios.get(`/api/v1/survey-response/dog/${dogId}/survey/${surveyId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });

            setSurveyResponses(prev => ({
                ...prev,
                [surveyId]: response.data
            }));

            // Initialize empty responses for fill mode
            if (canFill && (!response.data || response.data.length === 0)) {
                initializeEmptyResponses(surveyId);
            }
        } catch (err) {
            console.error(`Error fetching responses for survey ${surveyId}:`, err);
            setSurveyResponses(prev => ({
                ...prev,
                [surveyId]: []
            }));

            // Initialize empty responses for fill mode on error too
            if (canFill) {
                initializeEmptyResponses(surveyId);
            }
        } finally {
            setLoadingResponses(false);
        }
    };

    const initializeEmptyResponses = (surveyId) => {
        const survey = localSurveys.find(s => s.id === surveyId);
        if (survey && Array.isArray(survey.questions)) {
            const emptyResponses = {};
            survey.questions.forEach(question => {
                if (question.questionType === 'MULTIPLE_CHOICE') {
                    emptyResponses[question.id] = [];
                } else if (question.questionType === 'CHOICE_WITH_TEXT') {
                    emptyResponses[question.id] = { selectedOption: '', customText: '' };
                } else {
                    emptyResponses[question.id] = '';
                }
            });
            setResponses(prev => ({ ...prev, [surveyId]: emptyResponses }));
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return t('dogs:surveys.noDate');
        const date = new Date(dateString);
        return date.toLocaleDateString(i18n.language === 'pl' ? 'pl-PL' : 'en-US', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const toggleSurveyExpand = (surveyId) => {
        setExpandedSurvey(expandedSurvey === surveyId ? null : surveyId);
    };

    const getResponseForQuestion = (questionId, responses) => {
        if (!responses || responses.length === 0) return null;
        return responses.find(response => response.questionId === questionId);
    };

    const hasExistingResponses = (surveyId) => {
        return surveyResponses[surveyId] && surveyResponses[surveyId].length > 0;
    };

    // --- Fill mode handlers ---

    const handleResponseChange = (surveyId, questionId, value) => {
        setResponses(prev => ({
            ...prev,
            [surveyId]: {
                ...prev[surveyId],
                [questionId]: value
            }
        }));
    };

    const handleMultipleChoiceChange = (surveyId, questionId, option) => {
        setResponses(prev => {
            const currentResponses = prev[surveyId] || {};
            const current = Array.isArray(currentResponses[questionId]) ? currentResponses[questionId] : [];
            return {
                ...prev,
                [surveyId]: {
                    ...currentResponses,
                    [questionId]: current.includes(option)
                        ? current.filter(o => o !== option)
                        : [...current, option]
                }
            };
        });
    };

    const validateResponses = (surveyId) => {
        const survey = localSurveys.find(s => s.id === surveyId);
        if (!survey || !Array.isArray(survey.questions)) return false;

        for (const question of survey.questions) {
            const response = responses[surveyId]?.[question.id];
            if (question.required) {
                if (!response) {
                    toast.warning(t('dogs:surveys.pleaseAnswerQuestion', { question: question.questionText }));
                    return false;
                }
                if (question.questionType === 'MULTIPLE_CHOICE' && (!response || response.length === 0)) {
                    toast.warning(t('dogs:surveys.pleaseSelectOption', { question: question.questionText }));
                    return false;
                }
                if (question.questionType === 'CHOICE_WITH_TEXT') {
                    if (!response || ((!response.selectedOption || response.selectedOption === '') &&
                        (!response.customText || response.customText === ''))) {
                        toast.warning(t('dogs:surveys.pleaseAnswerQuestion', { question: question.questionText }));
                        return false;
                    }
                }
            }
        }
        return true;
    };

    const handleSubmitSurvey = async (surveyId) => {
        if (!validateResponses(surveyId)) return;

        setSubmitting(prev => ({ ...prev, [surveyId]: true }));

        try {
            const survey = localSurveys.find(s => s.id === surveyId);
            if (!survey || !Array.isArray(survey.questions)) {
                throw new Error('Survey not found or invalid');
            }

            const surveyResponsesData = survey.questions.map(question => {
                const response = responses[surveyId][question.id];

                if (question.questionType === 'CHOICE_WITH_TEXT') {
                    let textAnswer = "";
                    if (response.customText && response.customText.trim() !== "") {
                        textAnswer = response.customText;
                    } else if (response.selectedOption && response.selectedOption.trim() !== "") {
                        textAnswer = response.selectedOption;
                    }
                    return {
                        questionId: question.id,
                        textAnswer: textAnswer,
                        dateAnswer: null,
                        singleChoiceAnswer: response.selectedOption !== undefined ? response.selectedOption : "",
                        multipleChoiceAnswer: null
                    };
                } else {
                    return {
                        questionId: question.id,
                        textAnswer: question.questionType === 'TEXT' ? (response || "") : null,
                        dateAnswer: question.questionType === 'DATE' ? response : null,
                        singleChoiceAnswer: question.questionType === 'SINGLE_CHOICE' ? (response || "") : null,
                        multipleChoiceAnswer: question.questionType === 'MULTIPLE_CHOICE' ? (response || []) : null
                    };
                }
            });

            const submitData = {
                surveyId,
                dogId,
                responses: surveyResponsesData
            };

            await axios.post('/api/v1/survey-response/submit', submitData, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });

            toast.success(t('dogs:surveys.responsesSaved'));

            // Refresh the responses
            setSurveyResponses(prev => ({
                ...prev,
                [surveyId]: null
            }));
            fetchSurveyResponses(surveyId, dogId);
        } catch (err) {
            console.error('Error submitting responses:', err);
            toast.error(t('dogs:surveys.saveResponsesError'));
        } finally {
            setSubmitting(prev => ({ ...prev, [surveyId]: false }));
        }
    };

    const renderResponseValue = (response, questionType) => {
        if (!response) return <span className="dog-survey-no-response">{t('dogs:surveys.noResponse')}</span>;

        switch (questionType) {
            case 'TEXT':
                return response.textAnswer || <span className="dog-survey-no-response">{t('dogs:surveys.noResponse')}</span>;
            case 'DATE':
                return response.dateAnswer ? formatDate(response.dateAnswer) : <span className="dog-survey-no-response">{t('dogs:surveys.noResponse')}</span>;
            case 'SINGLE_CHOICE':
                return response.singleChoiceAnswer || <span className="dog-survey-no-response">{t('dogs:surveys.noResponse')}</span>;
            case 'MULTIPLE_CHOICE':
                return response.multipleChoiceAnswer && response.multipleChoiceAnswer.length > 0 ? (
                    <ul className="dog-survey-multiple-choice-list">
                        {response.multipleChoiceAnswer.map((option, idx) => (
                            <li key={idx}>{option}</li>
                        ))}
                    </ul>
                ) : <span className="dog-survey-no-response">{t('dogs:surveys.noResponse')}</span>;
            case 'CHOICE_WITH_TEXT':
                return (
                    <div className="dog-survey-choice-with-text-response">
                        {response.singleChoiceAnswer && (
                            <div className="dog-survey-selected-option">{t('dogs:surveys.choice')}: {response.singleChoiceAnswer}</div>
                        )}
                        {response.textAnswer && (
                            <div className="dog-survey-custom-text">{t('dogs:surveys.text')}: {response.textAnswer}</div>
                        )}
                        {!response.singleChoiceAnswer && !response.textAnswer && (
                            <span className="dog-survey-no-response">{t('dogs:surveys.noResponse')}</span>
                        )}
                    </div>
                );
            default:
                return <span className="dog-survey-no-response">{t('dogs:surveys.noResponse')}</span>;
        }
    };

    // --- Render helpers ---

    const renderFillInput = (survey, question) => (
        <div className="dog-survey-response-input" onClick={(e) => e.stopPropagation()}>
            {question.questionType === "TEXT" && (
                <input
                    type="text"
                    className="dog-survey-input"
                    placeholder={t('dogs:surveys.typeAnswer')}
                    value={responses[survey.id]?.[question.id] || ""}
                    onChange={(e) => handleResponseChange(survey.id, question.id, e.target.value)}
                />
            )}

            {question.questionType === "DATE" && (
                <CustomDatePicker
                    name={`survey-date-${question.id}`}
                    value={responses[survey.id]?.[question.id] || ""}
                    onChange={(e) => handleResponseChange(survey.id, question.id, e.target.value)}
                />
            )}

            {question.questionType === "SINGLE_CHOICE" && (
                <div className="dog-survey-single-choice">
                    {question.answerOptions && question.answerOptions.length > 0 ? (
                        question.answerOptions.map((option, idx) => (
                            <label key={idx} className="dog-survey-radio-container">
                                <input
                                    type="radio"
                                    name={`single-${question.id}`}
                                    value={option}
                                    checked={responses[survey.id]?.[question.id] === option}
                                    onChange={() => handleResponseChange(survey.id, question.id, option)}
                                />
                                <span className="dog-survey-option-text">{option}</span>
                            </label>
                        ))
                    ) : (
                        <p className="dog-survey-no-options">{t('dogs:surveys.noOptionsAvailable')}</p>
                    )}
                </div>
            )}

            {question.questionType === "MULTIPLE_CHOICE" && (
                <div className="dog-survey-multi-choice">
                    {question.answerOptions && question.answerOptions.length > 0 ? (
                        question.answerOptions.map((option, idx) => (
                            <label key={idx} className="dog-survey-checkbox-container">
                                <input
                                    type="checkbox"
                                    value={option}
                                    checked={Array.isArray(responses[survey.id]?.[question.id]) &&
                                        responses[survey.id]?.[question.id].includes(option)}
                                    onChange={() => handleMultipleChoiceChange(survey.id, question.id, option)}
                                />
                                <span className="dog-survey-option-text">{option}</span>
                            </label>
                        ))
                    ) : (
                        <p className="dog-survey-no-options">{t('dogs:surveys.noOptionsAvailable')}</p>
                    )}
                </div>
            )}

            {question.questionType === "CHOICE_WITH_TEXT" && (
                <div className="dog-survey-choice-with-text">
                    {question.answerOptions && question.answerOptions.length > 0 ? (
                        <>
                            <div className="dog-survey-options-section">
                                {question.answerOptions.map((option, idx) => (
                                    <label key={idx} className="dog-survey-radio-container" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="radio"
                                            name={`choice-with-text-${question.id}`}
                                            value={option}
                                            checked={responses[survey.id]?.[question.id]?.selectedOption === option}
                                            onChange={() => handleResponseChange(survey.id, question.id, {
                                                selectedOption: option,
                                                customText: responses[survey.id]?.[question.id]?.customText || ''
                                            })}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <span className="dog-survey-option-text">{option}</span>
                                    </label>
                                ))}
                            </div>
                            <div className="dog-survey-text-section">
                                <input
                                    type="text"
                                    className="dog-survey-input"
                                    placeholder={t('dogs:surveys.orTypeOwnAnswer')}
                                    value={responses[survey.id]?.[question.id]?.customText || ""}
                                    onChange={(e) => handleResponseChange(survey.id, question.id, {
                                        selectedOption: responses[survey.id]?.[question.id]?.selectedOption || '',
                                        customText: e.target.value
                                    })}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </>
                    ) : (
                        <input
                            type="text"
                            className="dog-survey-input"
                            placeholder={t('dogs:surveys.typeAnswer')}
                            value={responses[survey.id]?.[question.id]?.customText || ""}
                            onChange={(e) => handleResponseChange(survey.id, question.id, {
                                selectedOption: '',
                                customText: e.target.value
                            })}
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}
                </div>
            )}
        </div>
    );

    const renderReadOnlyQuestion = (question, response) => (
        <div className="dog-survey-question-content">
            {(question.questionType === 'SINGLE_CHOICE' ||
                    question.questionType === 'MULTIPLE_CHOICE' ||
                    question.questionType === 'CHOICE_WITH_TEXT') &&
                question.answerOptions &&
                question.answerOptions.length > 0 && (
                    <div className="dog-survey-answer-options">
                        <div className="dog-survey-options-label">{t('dogs:surveys.availableOptions')}:</div>
                        <ul>
                            {question.answerOptions.map((option, idx) => (
                                <li key={idx}>{option}</li>
                            ))}
                        </ul>
                    </div>
                )}

            <div className="dog-survey-response-section">
                <div className="dog-survey-response-label">{t('dogs:surveys.answer')}:</div>
                <div className="dog-survey-response-value">
                    {renderResponseValue(response, question.questionType)}
                </div>
            </div>
        </div>
    );

    if (!localSurveys || localSurveys.length === 0) {
        return (
            <div className="dog-surveys-container">
                <div className="dog-surveys-header-container">
                    <button
                        className="dog-surveys-assign-btn"
                        onClick={() => setIsAssignModalOpen(true)}
                    >
                        <FiPlus className="dog-surveys-btn-icon" />
                        <span>{t('dogs:surveys.assignSurvey')}</span>
                    </button>
                </div>
                <div className="dog-surveys-no-message">
                    <p>{t('dogs:surveys.noAssignedSurveys')}</p>
                </div>
                <AssignSurveyModal
                    isOpen={isAssignModalOpen}
                    onClose={() => setIsAssignModalOpen(false)}
                    dogId={dogId}
                    onAssigned={handleSurveyAssigned}
                />
            </div>
        );
    }

    return (
        <div className="dog-surveys-container">
            <div className="dog-surveys-header-container">
                <button
                    className="dog-surveys-assign-btn"
                    onClick={() => setIsAssignModalOpen(true)}
                >
                    <FiPlus className="dog-surveys-btn-icon" />
                    <span>{t('dogs:surveys.assignSurvey')}</span>
                </button>
            </div>
            <div className="dog-surveys-list">
                {localSurveys.map(survey => (
                    <div
                        key={survey.id}
                        className={`dog-survey-item ${expandedSurvey === survey.id ? 'dog-survey-expanded' : ''}`}
                        onClick={() => toggleSurveyExpand(survey.id)}
                    >
                        <div className="dog-survey-header">
                            <div className="dog-survey-info">
                                <h3 className="dog-survey-name">{survey.title}</h3>
                                <div className="dog-survey-meta">
                                    {survey.createdAt && (
                                        <span className="dog-survey-date-badge">
                                            {t('dogs:surveys.created')}: {formatDate(survey.createdAt)}
                                        </span>
                                    )}
                                    <span className="dog-survey-questions-badge">
                                        {t('dogs:surveys.questionCount', { count: survey.questions ? survey.questions.length : 0 })}
                                    </span>
                                    {canFill && hasExistingResponses(survey.id) && (
                                        <span className="dog-survey-status-badge filled">
                                            {t('dogs:surveys.filled')}
                                        </span>
                                    )}
                                    {canFill && !hasExistingResponses(survey.id) && surveyResponses[survey.id] !== undefined && (
                                        <span className="dog-survey-status-badge unfilled">
                                            {t('dogs:surveys.unfilled')}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="dog-survey-actions">
                                {expandedSurvey === survey.id ? (
                                    <IoChevronUpOutline
                                        size={20}
                                        className="dog-survey-expand-icon dog-survey-expanded"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleSurveyExpand(survey.id);
                                        }}
                                        title={t('dogs:surveys.hideDetails')}
                                    />
                                ) : (
                                    <IoChevronDownOutline
                                        size={20}
                                        className="dog-survey-expand-icon"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleSurveyExpand(survey.id);
                                        }}
                                        title={t('dogs:surveys.showDetails')}
                                    />
                                )}
                            </div>
                        </div>

                        {expandedSurvey === survey.id && (
                            <div className="dog-survey-details" onClick={(e) => canFill && e.stopPropagation()}>
                                <div className="dog-survey-details-content">
                                    {loadingResponses ? (
                                        <div className="dog-survey-loading-responses">
                                            <p>{t('dogs:surveys.loadingResponses')}</p>
                                        </div>
                                    ) : error ? (
                                        <div className="dog-survey-error-message">
                                            <p>{error}</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="dog-survey-questions-list">
                                                {survey.questions
                                                    .sort((a, b) => a.questionIndex - b.questionIndex)
                                                    .map((question, index) => {
                                                        const existingResponse = getResponseForQuestion(
                                                            question.id,
                                                            surveyResponses[survey.id]
                                                        );
                                                        const showFillInput = canFill && !hasExistingResponses(survey.id);

                                                        return (
                                                            <div key={question.id} className="dog-survey-question-item">
                                                                <div className="dog-survey-question-header">
                                                                    <div className="dog-survey-question-number">{index + 1}.</div>
                                                                    <div className="dog-survey-question-text">
                                                                        {question.questionText}
                                                                        {showFillInput && question.required && (
                                                                            <span className="dog-survey-required-mark">*</span>
                                                                        )}
                                                                    </div>
                                                                    <div className="dog-survey-question-type">
                                                                        {question.questionType === 'TEXT' && t('dogs:surveys.questionTypes.text')}
                                                                        {question.questionType === 'DATE' && t('dogs:surveys.questionTypes.date')}
                                                                        {question.questionType === 'SINGLE_CHOICE' && t('dogs:surveys.questionTypes.singleChoice')}
                                                                        {question.questionType === 'MULTIPLE_CHOICE' && t('dogs:surveys.questionTypes.multipleChoice')}
                                                                        {question.questionType === 'CHOICE_WITH_TEXT' && t('dogs:surveys.questionTypes.choiceWithText')}
                                                                    </div>
                                                                </div>

                                                                {showFillInput
                                                                    ? renderFillInput(survey, question)
                                                                    : renderReadOnlyQuestion(question, existingResponse)
                                                                }
                                                            </div>
                                                        );
                                                    })}
                                            </div>

                                            {canFill && !hasExistingResponses(survey.id) && (
                                                <div className="dog-survey-actions-section">
                                                    <button
                                                        className="dog-survey-submit-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSubmitSurvey(survey.id);
                                                        }}
                                                        disabled={submitting[survey.id]}
                                                    >
                                                        {submitting[survey.id] ? t('common:saving') : t('dogs:surveys.saveResponses')}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <AssignSurveyModal
                isOpen={isAssignModalOpen}
                onClose={() => setIsAssignModalOpen(false)}
                dogId={dogId}
                onAssigned={handleSurveyAssigned}
            />
        </div>
    );
};

export default DogSurveysList;
